import {
  AnimatedList,
  AnimatedListItem,
} from "@babackapp/customer-ui/components/animated-list";
import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { ImageInput } from "@babackapp/customer-ui/components/image-input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupTextarea,
} from "@babackapp/customer-ui/components/input-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, MessageCircleMoreIcon } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { Controller } from "react-hook-form";
import { useLineItemReasonForm } from "../../hooks/use-line-item-reason-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { ReasonOptionSelector } from "./reason-option-selector";

export function Reason() {
  const { form, onSubmit, reasons, selectedReason } = useLineItemReasonForm();
  const t = useCustomText();

  const showComment = !selectedReason || selectedReason.commentAvailable;
  const showImages = !selectedReason || selectedReason.photoAvailable;

  const errorMessages = [
    form.formState.errors.reasonId?.message,
    form.formState.errors.comment?.message,
    form.formState.errors.images?.message,
  ].filter(Boolean) as string[];

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("reason.title")}</HeaderTitle>
        <HeaderDescription>
          {t("reason.description")}
        </HeaderDescription>
      </Header>

      {errorMessages.length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            {errorMessages.map((msg, i) => <div key={i}>{msg}</div>)}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <AnimatedList>
          <AnimatedListItem>
            <Controller
              control={form.control}
              name="reasonId"
              render={({ field }) => (
                <ReasonOptionSelector
                  value={field.value ?? ""}
                  options={reasons.map((r) => ({
                    id: r.id,
                    label: r.label,
                    ...(r.isRetractation ? { gapBefore: 20 } : {}),
                  }))}
                  onChange={field.onChange}
                />
              )}
            />
          </AnimatedListItem>

          <AnimatePresence>
            {showComment && (
              <AnimatedListItem key="comment">
                <Controller
                  control={form.control}
                  name="comment"
                  render={({ field }) => (
                    <InputGroup className="h-auto">
                      <InputGroupTextarea
                        placeholder={
                          selectedReason?.requiresComment
                            ? t("reason.commentRequired")
                            : t("reason.commentOptional")
                        }
                        value={field.value ?? ""}
                        onChange={(e) => field.onChange(e.target.value || null)}
                        aria-invalid={!!form.formState.errors.comment}
                      />
                      <InputGroupAddon align="block-start">
                        <InputGroupText>{t("reason.commentLabel")}</InputGroupText>
                        <MessageCircleMoreIcon />
                      </InputGroupAddon>
                    </InputGroup>
                  )}
                />
              </AnimatedListItem>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showImages && (
              <AnimatedListItem key="images">
                <Controller
                  control={form.control}
                  name="images"
                  render={({ field }) => (
                    <ImageInput onChange={field.onChange} />
                  )}
                />
              </AnimatedListItem>
            )}
          </AnimatePresence>
        </AnimatedList>

        <AnimatedButtonRow exit={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-confirm-button"
            disableVariants={{ exit: true }}
          >
            <Button type="submit">
              <EnterIcon />
              {t("common.confirm")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
